/* NGĂN HỒ SƠ KHÁCH (tab: Cuộc gọi · Nhắn tin · Nhu cầu · Báo giá… ) */
import React, { useState } from 'react';
import {
  Alert, Button, Collapse, DatePicker, Descriptions, Input, InputNumber, Radio, Select, Tabs, Timeline,
} from 'antd';
import dayjs from 'dayjs';
import { Image as ImageIcon, PanelRightClose, Send, X } from 'lucide-react';
import {
  CN, CODES, CUS, IMG, LYDO, MOCS, NGUONG, SCRIPT, SMAP, SVC, TPL, svcOf,
} from './Data';
import { ChanButtons } from './Common';
import { defSub, flaggedMsgs, useS } from './Store';
import {
  BOX, Chips, Hint, KVList, Link, Lock, MiniTable, Note, PhotoBox, Q, Q_RING, QR, Rich, SChip, SecTitle,
  TXT_MUTED, TXT_NAVY, TXT_OK, cx, fmt, money,
} from './UI';
import type { BgState, Customer, PhotoRow, Profile } from './Types';

const SAY = 'my-1.5 border-l-4 border-[#4091AA] bg-[#EEF5F8] px-2 py-1.5 text-[12.5px] leading-relaxed dark:bg-slate-700/50';
const DONT = 'my-1.5 border-l-4 border-[#B03A2E] bg-[#FBEFEF] px-2 py-1.5 text-[12.5px] leading-relaxed dark:bg-red-950/30';
const COND = 'my-1.5 space-y-2 border-l-4 border-[#2E75B6] bg-[#EEF5F8] p-2 text-[12.5px] dark:bg-slate-700/40';
const STEP = 'my-1.5 border-l-4 border-[#4091AA] bg-[#F7FAFC] px-2.5 py-2 text-[12.5px] leading-[1.9] dark:bg-slate-700/30';
const CHI_NHANH = [CN, 'Quận 2 [?]'];

const CallTab: React.FC<{ c: Customer }> = ({ c }) => {
  const S = useS();
  const call = S.call;
  if (!call) return null;
  const f = call.f;
  const sc = SCRIPT[c.mang.toLowerCase()];
  const dt = (k: string) => (
    <DatePicker size="small" showTime format="DD/MM/YYYY HH:mm" value={f[k] ? dayjs(f[k]) : null} onChange={(d) => S.setCallField(k, d ? d.format('YYYY-MM-DD HH:mm') : '')} />
  );
  const sel = (k: string, opts: string[], w = 170) => (
    <Select size="small" style={{ width: w }} placeholder="— chọn —" value={f[k] || undefined} onChange={(v) => S.setCallField(k, v)} options={opts.map((o) => ({ value: o, label: o }))} />
  );
  const cond = (code: string) => {
    if (call.code !== code) return null;
    if (code === 'KN-01') return <div className={COND}><div className="flex flex-wrap items-center gap-2">Ngày giờ {dt('lhNgay')} Chi nhánh {sel('lhCn', CHI_NHANH)} Mảng / dịch vụ {sel('lhMang', ['Newhair', 'Clinic', 'PTTM', 'Partnership'], 140)}</div></div>;
    if (code === 'KN-02') return <div className={COND}><div className="flex flex-wrap items-center gap-2">Ngày giờ gọi lại (bắt buộc — R02) {dt('hgNgay')}</div></div>;
    if (code === 'KN-07') return <div className={COND}><div className="flex flex-wrap items-center gap-2">Hệ thống tạo phiếu Chuyển bác sĩ (R04). Nhóm vấn đề {sel('kn07', ['Bệnh nền', 'Đang điều trị', 'Khác'], 150)} <Q /></div></div>;
    return null;
  };
  return (
    <div>
      <Collapse
        size="small"
        defaultActiveKey={['s']}
        className='dark:[&_.ant-collapse-content-box]:!bg-[#0F172A] dark:[&_.ant-collapse-content-box]:text-white'
        items={[{
          key: 's',
          label: <b className={TXT_NAVY}>Kịch bản theo mảng <Q>chờ ban hành Script Q3</Q></b>,
          children: (
            <div>
              <div className={SAY}><b>Bước 0:</b> nghe nhu cầu → xác định mảng → dùng đúng cách gọi tên gói của mảng.</div>
              <div className={SAY}><b>Hỏi lý do:</b> “Điều gì khiến anh/chị muốn tìm hiểu [tên dịch vụ] vào lúc này ạ?”</div>
              <p className="mb-1 mt-2"><b>Mảng {c.mang}</b></p>
              <p className="my-1"><Rich text={sc.goi} /></p>
              <div className={SAY}><Rich text={sc.say} /></div>
              <div className={DONT}><b>KHÔNG LÀM:</b> không hứa kết quả · không so sánh với nơi khác · không giảm giá theo % · không tự chẩn đoán · không ghi chi tiết bệnh lý.</div>
            </div>
          ),
        }]}
      />
      <SecTitle className="mt-3">Kết quả cuộc gọi — bắt buộc trước khi đóng (R01) <Q>danh sách mã</Q></SecTitle>
      <Radio.Group className="w-full" value={call.code} onChange={(e) => S.setCallCode(e.target.value)}>
        {CODES.map((g) => (
          <div key={g.grp} className="mb-1">
            <div className={cx('mb-0.5 mt-2 font-bold', TXT_NAVY)}>{g.grp}</div>
            {g.items.map((it) => (
              <div key={it.code}>
                <Radio value={it.code}>{it.code} {it.label}</Radio>
                {cond(it.code)}
              </div>
            ))}
          </div>
        ))}
      </Radio.Group>
      <div className="mt-2">
        <b>Nhóm lý do làm lúc này</b> <Q />
        <Select className="mt-1 !w-full" size="small" placeholder="— chọn —" value={f.lydo || undefined} onChange={(v) => S.setCallField('lydo', v)} options={LYDO.map((o) => ({ value: o, label: o }))} />
      </div>
      <div className="mt-2">
        <b>Ghi chú</b>
        <Input.TextArea rows={2} className="mt-1" placeholder="Ngắn gọn; không ghi chi tiết bệnh lý" value={f.note ?? ''} onChange={(e) => S.setCallField('note', e.target.value)} />
      </div>
      <div className="mt-2 flex gap-2">
        <Button type="primary" onClick={() => S.saveCall()}>Lưu kết quả</Button>
        <Button onClick={() => S.saveCall(true)}>Lưu và gọi tiếp</Button>
      </div>
    </div>
  );
};

const MsgTab: React.FC<{ c: Customer }> = ({ c }) => {
  const S = useS();
  const s = S.msOf(c.ma);
  const abnormal = flaggedMsgs(s.th);
  return (
    <div>
      <Note className="!mt-0">
        Kênh <b>{s.chan}</b> · tin 2 chiều tự đồng bộ vào hồ sơ và Lịch sử chạm (cách kết nối do BUSO quyết) · khách trả lời → về đúng người phụ trách {c.pt}, không vào nhóm Trực page (R25) · chuẩn phản hồi ≤ 5 phút trong ca
      </Note>
      {abnormal && <Alert type="error" showIcon={false} className="mb-2" message={<b>Tin có dấu hiệu bất thường → báo CSKH / bác sĩ (R27).</b>} />}
      <div className="max-h-80 overflow-auto rounded border border-[#D5E2EA] bg-[#F3F6F9] p-2 dark:border-slate-700 dark:bg-slate-900">
        {!s.th.length && <Note>Chưa có tin nhắn.</Note>}
        {s.th.map((m, i) => (
          <div key={i} className={cx('my-1.5 max-w-[85%] rounded-md px-2.5 py-1.5 text-[12.5px] leading-snug', m.from === 'k' ? 'border border-[#D5E2EA] bg-white dark:border-slate-600 dark:bg-slate-800' : 'ml-auto bg-[#DCEAF1] dark:bg-sky-900/40')}>
            <div className={cx('mb-0.5 text-xs', TXT_MUTED)}>{m.from === 'k' ? 'Khách' : 'Nhân viên'} · {m.time}</div>
            {m.img ? (
              <>
                <PhotoBox ma={c.ma} label={m.img[0]} size="sm" />
                {c.phong === 'Telesales' && <Button className="mt-1" size="small" icon={<ImageIcon size={12} />} onClick={() => S.saveImg(c.ma, i)}>Lưu vào hồ sơ</Button>}
                {m.note && <div className={cx('mt-1 text-xs', TXT_OK)}><Rich text={m.note} /></div>}
              </>
            ) : m.text}
            {m.from === 'n' && (
              <div className={cx('mt-0.5 text-xs', TXT_NAVY)}>
                {['Đã gửi', 'Đã nhận', 'Khách trả lời'].map((x, k) => (
                  <React.Fragment key={x}>{k > 0 && ' → '}{x === (m.status ?? 'Đã gửi') ? <b>{x}</b> : x}</React.Fragment>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <b className="shrink-0">Mẫu tin</b>
        <Select
          className="!flex-1" size="small" allowClear placeholder="— chọn mẫu (Telesales) —" value={s.tpl || undefined}
          onChange={(v) => S.pickTpl(c.ma, v ?? '')} options={TPL.map((t) => ({ value: t[0], label: t[1] }))}
        />
      </div>
      <Input.TextArea rows={4} className="mt-1.5" placeholder="Soạn tin; mẫu tự điền tên, dịch vụ, giá từ, hạn ưu đãi, ngày hẹn" value={s.draft} onChange={(e) => S.setDraft(c.ma, e.target.value)} />
      <div className="mt-1 flex items-center gap-2">
        <Button type="primary" icon={<Send size={13} />} onClick={() => S.sendMsg(c.ma)}>Gửi</Button>
        <span className={cx('text-xs', TXT_MUTED)}>Không nêu tên máy, không hứa kết quả.</span>
      </div>
      <SecTitle className="mt-3">Mã kết quả lượt nhắn — bắt buộc (R24 · R01)</SecTitle>
      <Radio.Group className="w-full" value={s.code} onChange={(e) => S.setMsgCode(c.ma, e.target.value)}>
        {CODES.map((g) => (
          <div key={g.grp}>
            <div className={cx('mb-0.5 mt-2 font-bold', TXT_NAVY)}>{g.grp}</div>
            <div className="flex flex-col">{g.items.map((it) => <Radio key={it.code} value={it.code}>{it.code} {it.label}</Radio>)}</div>
          </div>
        ))}
      </Radio.Group>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button type="primary" onClick={() => S.saveMsg(c.ma)}>Lưu lượt nhắn</Button>
        <span className={cx('text-xs', TXT_MUTED)}>Tin gửi quá X giờ <Q /> chưa có trả lời → tự tạo việc chạm lại (R24)</span>
      </div>
    </div>
  );
};

const BgTab: React.FC<{ c: Customer; p: Profile }> = ({ c, p }) => {
  const S = useS();
  const b = S.bgOf(c.ma);
  const v = svcOf(b);
  const lock = !!v.lock;
  const gi = lock ? 0 : parseFloat(b.giam) || 0;
  let err = '';
  let over = false;
  if (gi > 30) err = 'Tổng giảm vượt 30% mỗi dịch vụ (40% mỗi combo) — chặn, không xin duyệt được (CS giá 7.2, R31).';
  else if (gi > NGUONG) over = true;
  if (gi > 0 && !b.lydo && !err) err = 'Bắt buộc ghi lý do giảm (CS giá 7.3).';
  const sau = v.gia == null ? null : Math.round(v.gia * (1 - gi / 100));
  const canSend = v.gia != null && !err && !(over && b.status !== 'Đã duyệt') && b.status !== 'Chờ duyệt';
  const cc = b.coc;
  const canEdit = c.phong === 'Telesales';
  const upd = (patch: Partial<BgState>) => S.updBg(c.ma, patch);
  return (
    <div>
      <SecTitle>Lịch sử báo giá</SecTitle>
      <MiniTable
        head={['Ngày', 'Mã', 'Gói / dịch vụ', 'Giá', 'Mức trợ giá', 'Người báo', 'Trạng thái']}
        rows={p.bg}
        nowrap={[1]}
        empty={`Chưa có báo giá.${c.mang === 'PTTM' ? ' Giá PTTM chưa có trong Công cụ báo giá — chỉ mời khám [?]' : ''}`}
      />
      <SecTitle className="mt-3">Cọc <Q>chính sách cọc</Q></SecTitle>
      <MiniTable head={['Mã cọc', 'Ngày', 'Số tiền', 'Mục đích', 'Trạng thái']} rows={p.coc} nowrap={[0]} empty="Chưa có yêu cầu cọc." />
      {!canEdit && <Note>Khách thuộc danh sách CSKH — Telesales chỉ xem, không tạo báo giá / cọc (R34).</Note>}
      {canEdit && (
        <div className={cx(BOX, 'mt-3 !border-[#9FBFD0] p-2.5')}>
          <SecTitle>Báo giá tham khảo mới — BG-1015-01 {b.status === 'Nháp' ? '(nháp)' : ''}</SecTitle>
          <div className="mb-1.5 text-[12.5px]">Báo giá: <Chips list={['Nháp', 'Chờ duyệt', 'Đã gửi', 'Khách phản hồi', 'Đã chuyển cơ sở']} cur={b.status} /></div>

          <div className={STEP}>
            <b>1. Chọn dịch vụ</b><br />
            <span className="mr-2">Mảng</span>
            <Select size="small" style={{ width: 130 }} value={b.mang} onChange={(m) => upd({ mang: m, dv: SVC[m]?.[0]?.[0] ?? '', giam: '' })} options={Object.keys(SVC).map((m) => ({ value: m, label: m }))} />
            <span className="mx-2">Dịch vụ</span>
            <Select size="small" style={{ minWidth: 220, maxWidth: '100%' }} value={b.dv} onChange={(d) => upd({ dv: d, giam: '' })} options={(SVC[b.mang] ?? []).map((x) => ({ value: x[0], label: x[0] }))} />
            <div className="mt-1">
              Giá từ <Input size="small" disabled className="!w-36 font-bold" value={v.gia == null ? 'chưa có' : money(v.gia)} />{' '}
              <span className={cx('text-xs', TXT_MUTED)}>tự điền từ Công cụ báo giá: hạng bác sĩ Chuẩn, cấp thấp nhất · <b>không gõ giá tay</b> (R30) · giá trên mockup là minh hoạ</span>
            </div>
            <div className={cx('text-xs', TXT_MUTED)}>
              Luôn kèm câu: <i>“Giá tham khảo, báo giá chính thức sau khám.”</i> Telesales không báo theo mức độ, số nang — bác sĩ xác định (CS giá 1.4).
              {v.gia == null && <span className="text-[#B03A2E]"> {v.lock} <Q /> — chỉ mời khám, chưa tạo được báo giá</span>}
            </div>
          </div>

          <div className={STEP}>
            <b>2. Ưu đãi</b><br />
            Chương trình đang chạy{' '}
            <Select size="small" style={{ width: 280, maxWidth: '100%' }} allowClear placeholder="— không —" value={b.ct || undefined} onChange={(x) => upd({ ct: x ?? '' })} options={[{ value: 'Ưu đãi tặng kèm buổi chăm sóc (minh hoạ)', label: 'Ưu đãi tặng kèm buổi chăm sóc (minh hoạ)' }]} /><br />
            Giảm trong quyền{' '}
            <InputNumber size="small" min={0} max={100} disabled={lock} placeholder={lock ? 'khoá' : undefined} value={lock || b.giam === '' ? null : Number(b.giam)} onChange={(x) => upd({ giam: x == null ? '' : String(x) })} className="!w-20" /> %{' '}
            <span className={cx('rounded px-1.5', Q_RING)}>ngưỡng Tư vấn Online ≤ {NGUONG}% <Q>tham số cấu hình</Q></span>
            {lock && <Lock>Ô giảm % khoá: {v.lock} — không giảm % thẳng (CS giá 7.2). Dùng ưu đãi tặng kèm nếu có chương trình.</Lock>}
            Lý do giảm (bắt buộc khi giảm) <Input size="small" className="!w-3/5" value={b.lydo} onChange={(e) => upd({ lydo: e.target.value })} />
            {err && <Alert className="!mt-1.5" type="error" showIcon={false} message={<b>{err}</b>} />}
            {over && !err && (
              <Alert className="!mt-1.5" type="error" showIcon={false} message={<span><b>Giảm {gi}% vượt quyền Tư vấn Online ({NGUONG}%). Gửi Trưởng phòng duyệt (CS giá 7.3).</b>{' '}<Button size="small" danger type="primary" onClick={() => S.bgAct(c.ma, 'duyet')}>Xin duyệt</Button></span>} />
            )}
            {b.status === 'Chờ duyệt' && <div className="mt-1 text-xs font-bold text-[#C77700]">Chờ duyệt — Trưởng phòng Telesales xử lý; chưa gửi khách được.</div>}
            <div>Giá sau ưu đãi: <b>{money(sau)}</b> <span className={cx('text-xs', TXT_MUTED)}>trần: 30% mỗi dịch vụ, 40% mỗi combo</span></div>
          </div>

          <div className={STEP}>
            <b>3. Gửi khách</b> · hạn ưu đãi <Input size="small" className="!w-24" value={b.han} onChange={(e) => upd({ han: e.target.value })} />{' '}
            <Button size="small" type="primary" disabled={!canSend} onClick={() => S.bgAct(c.ma, 'gui')}>Gửi qua Zalo (mẫu báo giá)</Button>{' '}
            {c.nn && <Button size="small" onClick={() => S.bgAct(c.ma, 'guiw')}>WhatsApp</Button>}
            <div className={cx('text-xs', TXT_MUTED)}>Gần hết hạn → gợi ý nhắc ưu đãi (R18). Báo giá đã gửi không sửa được; muốn đổi → tạo bản mới, bản cũ thành “Đã thay thế”. Huỷ phải có lý do, không xoá.</div>
          </div>

          <div className={STEP}>
            <b>4. Yêu cầu cọc</b> <Q>chính sách cọc</Q><br />
            Mục đích{' '}
            <Select size="small" style={{ width: 120 }} value={b.mucdich} onChange={(x) => upd({ mucdich: x })} options={['Giữ lịch', 'Giữ ưu đãi', 'Giữ bác sĩ'].map((x) => ({ value: x, label: x }))} />
            <span className="mx-2">Số tiền</span>
            <Input size="small" className="!w-32" placeholder="theo chính sách cọc" value={b.tienIn} onChange={(e) => upd({ tienIn: e.target.value })} />{' '}
            <Button size="small" type="primary" onClick={() => S.bgAct(c.ma, 'qr')}>Tạo mã QR</Button>
            {cc && (
              <>
                <div className={cx('my-1.5 text-xs', TXT_MUTED)}>Cọc: <Chips list={['Chờ chuyển khoản', 'Chờ đối soát', 'Đã nhận', 'Đã trừ vào hoá đơn']} cur={cc.st} /> · Đã hoàn / Khách bỏ cọc</div>
                <div className="flex flex-wrap items-start gap-3">
                  <QR seed={20418} />
                  <Descriptions size="small" column={1} bordered labelStyle={{ width: 110 }} items={[
                    { key: 'a', label: 'Số tiền', children: <b>{cc.tien}</b> },
                    { key: 'b', label: 'Mục đích', children: cc.mucd },
                    { key: 'c', label: 'Nội dung CK', children: <span><b>KH20418 BG101501</b> (tự gắn mã khách + mã báo giá)</span> },
                    { key: 'd', label: 'Tài khoản', children: <span>Công ty Cổ phần Bệnh viện Thẩm mỹ Khơ Thị <Q>số tài khoản</Q></span> },
                  ]} />
                </div>
              </>
            )}
          </div>

          <div className={STEP}>
            <b>5. Biên lai và đối soát</b><br />
            {cc ? <Button size="small" disabled={cc.st !== 'Chờ chuyển khoản'} onClick={() => S.bgAct(c.ma, 'bl')}>Tải ảnh biên lai</Button> : <span className={cx('text-xs', TXT_MUTED)}>Tạo yêu cầu cọc trước.</span>}
            {cc?.bl && <span className="ml-2 inline-flex items-center gap-2 align-middle"><PhotoBox ma={c.ma} label="Biên lai" size="sm" /><b className="text-[#C77700]">Chờ đối soát</b></span>}
            <div className={cx('text-xs', TXT_MUTED)}>Chỉ Kế toán đối soát (sao kê / POS) mới chuyển “Đã nhận cọc”. Telesales không có nút “đã cọc” (R32).</div>
          </div>

          <div className={STEP}>
            <b>6. Chuyển cơ sở</b><br />
            <Button size="small" onClick={() => S.bgAct(c.ma, 'cs')}>Khách đến — chuyển Tư vấn cơ sở</Button>
            <div className={cx('text-xs', TXT_MUTED)}>Phiếu báo giá chính thức (bác sĩ xác định, ký) nối với BG-1015-01 → chỉ số chênh lệch giá báo online và giá chốt (R33).</div>
          </div>
          <Note>Telesales không thấy: giá vốn, biên lợi nhuận, Bảng giá gốc đầy đủ, hạng bác sĩ (CS giá 10.5).</Note>
        </div>
      )}
    </div>
  );
};

const ImgTab: React.FC<{ c: Customer }> = ({ c }) => {
  const D = IMG[c.ma] ?? { dv: '—', m: {} as Record<string, PhotoRow[]> };
  const mocs = [...MOCS, ...(c.mang === 'Newhair' ? ['Dài hạn Newhair'] : []), 'Bất thường'];
  const visible = (m: string) => m === 'Trước tư vấn'; /* Telesales chỉ thấy ảnh trước tư vấn */
  const hid = mocs.reduce((n, m) => n + (visible(m) ? 0 : (D.m[m] ?? []).length), 0);
  return (
    <div>
      <Note className="!mt-0">Dịch vụ gắn: <b>{D.dv}</b> · không tải xuống, ảnh đóng dấu mã khách, mỗi lần mở ảnh ghi nhật ký · Đồng ý dùng cho truyền thông: <b>Không</b> (mặc định) <Q>mẫu đồng ý riêng</Q></Note>
      {mocs.filter(visible).map((m) => {
        const a = D.m[m] ?? [];
        return (
          <div key={m} className="mb-2 ml-1 border-l-[3px] border-[#7DB4C7] pl-3">
            <div className={cx('text-[12.5px]', TXT_NAVY)}><b>{m}</b>{a.length ? ` · ${a[0][2]}` : ''}</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {a.length ? a.map((x, i) => <PhotoBox key={i} ma={c.ma} label={x[0]} title={`${x[1]} · ${x[2]}`} />) : <PhotoBox ma={c.ma} label="Chưa có ảnh" empty />}
            </div>
            {a.length > 0 && <div className={cx('text-xs', TXT_MUTED)}>Người tải: {a[0][3]}</div>}
          </div>
        );
      })}
      {hid > 0 && (
        <div className="my-2 border border-dashed border-[#E07B00] bg-[#FFF4E5] px-2.5 py-1.5 text-[12.5px] dark:bg-amber-950/40">
          Ảnh sau dịch vụ và ảnh mốc CSKH: <b>{hid} ảnh — chỉ CSKH và bác sĩ xem</b> <Q>Telesales thấy số lượng</Q>
        </div>
      )}
      <Note>Góc chụp: Newhair trước / đỉnh / 2 bên / sau · mặt thẳng / nghiêng trái / nghiêng phải <Q>Khối y khoa chốt</Q>. Ảnh khám do bác sĩ chụp: CMS chỉ hiện đường dẫn sang hồ sơ bệnh án <Q /></Note>
    </div>
  );
};

const DvTab: React.FC<{ p: Profile }> = ({ p }) => {
  if (!p.dv.length) {
    const m = p.moi ?? { quantam: [], noikhac: [], mocmh: [] };
    return (
      <div>
        <Hint><b>Khách mới — chưa phát sinh dịch vụ</b></Hint>
        <SecTitle>Dịch vụ quan tâm <span className={cx('text-xs font-normal', TXT_MUTED)}>(lấy từ phiếu nhu cầu)</span></SecTitle>
        <MiniTable head={['#', 'Dịch vụ quan tâm']} rows={m.quantam.map((x, i) => [String(i + 1), x])} empty="—" />
        <SecTitle className="mt-3">Đã làm ở nơi khác <span className={cx('text-xs font-normal', TXT_MUTED)}>(khách kể · chỉ ghi loại dịch vụ, không ghi tên nơi làm)</span></SecTitle>
        <MiniTable head={['#', 'Nội dung']} rows={m.noikhac.map((x, i) => [String(i + 1), x])} empty="—" />
        <SecTitle className="mt-3">Dự kiến mốc tái khám sau khi làm <Q>minh hoạ quy trình</Q></SecTitle>
        <MiniTable head={['Mốc', 'Việc']} rows={m.mocmh} empty="—" />
        <div className="mt-3"><KVList items={[['Chi tiêu', 'Chưa phát sinh'], ['Thẻ liệu trình', 'Chưa có']]} /></div>
      </div>
    );
  }
  const ch = p.chi;
  return (
    <div>
      <SecTitle>Dịch vụ đã làm</SecTitle>
      <MiniTable head={['Ngày', 'Chi nhánh', 'Mảng', 'Gói / dịch vụ', 'Buổi', 'Người thực hiện', 'Giá trị', 'Thanh toán']} rows={p.dv} nowrap={[0, 6]} empty="—" />
      <SecTitle className="mt-3">Thẻ liệu trình</SecTitle>
      <MiniTable head={['Mã thẻ', 'Gói', 'Đã dùng', 'Còn lại', 'Giá trị còn lại', 'Hạn', 'Buổi kế tiếp dự kiến']} rows={p.the.map((r) => r.slice(0, 7))} empty="Không có thẻ liệu trình." />
      <SecTitle className="mt-3">Sản phẩm mang về (Homecare)</SecTitle>
      <MiniTable head={['Ngày', 'Sản phẩm', 'Số lượng']} rows={p.hc} empty="Chưa mua sản phẩm mang về." />
      <SecTitle className="mt-3">Chi tiêu</SecTitle>
      {ch && (
        <KVList items={[['Hạng chi tiêu', `**${ch.hang}**`], ['Số lần dùng dịch vụ', ch.solan], ['Lần gần nhất', ch.gan], ['Số tiền', 'Ẩn — số tiền chỉ Trưởng phòng và CCO xem [?]']]} />
      )}
      <SecTitle className="mt-3">Mốc tái khám</SecTitle>
      <MiniTable head={['Mốc', 'Ngày dự kiến', 'Trạng thái']} rows={p.moc.map((r) => [r[0], r[1], r[2].includes('Quá hạn') ? `!!${r[2]}!!` : r[2]])} empty="Chưa có mốc tái khám." />
    </div>
  );
};

const ProfileBody: React.FC<{ c: Customer; sub: string }> = ({ c, sub }) => {
  const S = useS();
  const p = S.profiles[c.ma];
  const calling = S.call?.ma === c.ma;
  const hasMsg = !!S.ms[c.ma];
  const [showPhone, setShowPhone] = useState(false);
  const eff = sub === 'call' && !calling ? defSub(c) : sub === 'msg' && !hasMsg ? defSub(c) : sub;
  const readOnly = c.phong === 'CSKH';
  const orange = 'font-bold text-[#8A4B00] dark:text-amber-300';
  const items = [
    ...(calling ? [{ key: 'call', label: <span className={orange}>Cuộc gọi</span> }] : []),
    ...(hasMsg ? [{ key: 'msg', label: <span className={orange}>Nhắn tin · {S.ms[c.ma].chan}</span> }] : []),
    { key: 'nhu', label: 'Nhu cầu' }, { key: 'bg', label: 'Báo giá và cọc' }, { key: 'lich', label: 'Lịch hẹn' },
    { key: 'img', label: 'Hình ảnh' }, { key: 'dv', label: 'Dịch vụ và thẻ' }, { key: 'cham', label: 'Lịch sử chạm' }, { key: 'yk', label: 'Cờ y khoa' },
  ];
  const strip = (IMG[c.ma]?.m['Trước tư vấn'] ?? []).slice(-4);
  const hidCnt = Object.entries(IMG[c.ma]?.m ?? {}).reduce((n, [k, v]) => n + (k === 'Trước tư vấn' ? 0 : v.length), 0);
  const kenh = c.kenh || 'Gọi';

  let body: React.ReactNode = null;
  if (eff === 'call') body = <CallTab c={c} />;
  else if (eff === 'msg') body = <MsgTab c={c} />;
  else if (eff === 'img') body = <ImgTab c={c} />;
  else if (eff === 'bg') body = <BgTab c={c} p={p} />;
  else if (eff === 'nhu') body =
    <>
      <Note className="!mt-0">Phiếu nhu cầu theo mảng <b>{c.mang}</b> — trường cố định</Note>
      <KVList items={p.nhu} />
    </>;
  else if (eff === 'lich') body = (
    <>
      <MiniTable head={['Thời gian', 'Loại', 'Chi nhánh', 'Trạng thái']} rows={p.lich} empty="Chưa có lịch hẹn." nowrap={[0]} />
      <div className="mt-2"><KVList items={[['Số lần không đến', p.khongden ? `!!${p.khongden}!!` : '0'], ['Số lịch', String(p.lich.length)]]} /></div>
    </>
  );
  else if (eff === 'dv') body = <DvTab p={p} />;
  else if (eff === 'cham') body = (
    <>
      <Note className="!mt-0">Dòng thời gian gộp cả 2 phòng: cuộc gọi, chat, Zalo, lịch, đổi mã (R14)</Note>
      <Timeline className="!mt-3" items={p.tl.map(([t, x]) => ({
        children:
          <div className="text-[12.5px] leading-snug">
            <span className={cx('text-xs', TXT_MUTED)}>{t}</span>
            <br />
            <Rich text={x} />
          </div>
      }))} />
    </>
  );
  else if (eff === 'yk') body = (
    <>
      <KVList items={p.cs.filter((x) => ['Cờ cần bác sĩ xem trước', 'Phiếu chuyển bác sĩ', 'Consent đã ký', 'Đồng ý dùng ảnh truyền thông'].includes(x[0]))} />
      <Note>App Telesales chỉ hiện cờ y khoa. CSAT, khiếu nại, chi tiết chăm sóc nằm ở App CSKH.</Note>
    </>
  );

  return (
    <div>
      {/* đầu hồ sơ */}
      <div className="rounded-md border border-[#D5E2EA] bg-[#EEF5F8] p-2.5 dark:border-slate-700 dark:bg-slate-800/70">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <span className={cx('text-base font-bold', TXT_NAVY)}>{c.ten}</span> <span className={cx('text-xs', TXT_MUTED)}>· xưng hô: {p.xung}</span><br />
            <b>{c.ma}</b> · {c.sdt}{' '}
            <Button size="small" onClick={() => setShowPhone((x) => !x)}>Hiện số</Button>
            {showPhone && <div className="text-xs text-[#B03A2E] dark:text-red-400">Vai này không được hiện số đầy đủ; gọi qua tổng đài (mục 10) <Q /></div>}
          </div>
          <div className="text-right">
            <SChip s={c.s} /> <span className={cx('text-xs', TXT_MUTED)}>{SMAP[c.s] ?? ''}</span><br />
            <span className={cx('text-xs', TXT_MUTED)}>Ở mã này: {p.ngayS}</span>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 text-[12.5px] sm:grid-cols-[130px_1fr]">
          {([
            ['Phòng đang phụ trách', <><b>{c.phong}</b> (R22)</>],
            ['Mảng', <b>{c.mang}</b>],
            ['Kênh khách muốn', <><b>{kenh}</b>{c.nn ? ` · ${c.nn}` : ''} <span className={cx('text-xs', TXT_MUTED)}>(Gọi / Zalo / WhatsApp / Chỉ nhắn tin)</span></>],
            ['Loại khách', p.loai], ['Thẻ Member', p.member], ['Phụ trách', p.pt], ['Nguồn', p.nguon],
          ] as [string, React.ReactNode][]).map(([k, v]) => (
            <React.Fragment key={k}><span className={TXT_MUTED}>{k}</span><span>{v}</span></React.Fragment>
          ))}
        </div>
        {p.flags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {p.flags.map(([f, cls]) => (
              <span key={f} className={cx('rounded border px-1.5 text-xs font-bold', cls === 'info' ? 'border-[#9FBFD0] bg-white text-[#1F4E79] dark:bg-slate-800 dark:text-sky-300' : 'border-[#E8B4AE] bg-[#FBEFEF] text-[#B03A2E] dark:bg-red-950/40 dark:text-red-300')}>{f}</span>
            ))}
          </div>
        )}
        {readOnly && (
          <div className="mt-1.5 border border-dashed border-[#E07B00] bg-[#FFF4E5] px-2.5 py-1.5 text-[12.5px] dark:bg-amber-950/40">
            <b>Chỉ đọc</b> — khách thuộc danh sách CSKH (đã thanh toán dịch vụ). Telesales xem hồ sơ, không gọi / nhắn / sửa (R34).
          </div>
        )}
        {strip.length > 0 && (
          <div className="mt-2 flex cursor-pointer flex-wrap items-center gap-1.5" onClick={() => S.setSub(c.ma, 'img')}>
            {strip.map((x, i) => <PhotoBox key={i} ma={c.ma} label={x[0]} size="sm" title={`${x[1]} · ${x[2]}`} />)}
            {hidCnt > 0 && <span className={cx('text-xs', TXT_MUTED)}>+{hidCnt} ảnh — chỉ CSKH và bác sĩ xem</span>}
            <Link onClick={() => S.setSub(c.ma, 'img')}>Mở tab Hình ảnh</Link>
          </div>
        )}
        {!readOnly && (
          <>
            {!calling && <div className="mt-2"><ChanButtons c={c} big /></div>}
            <div className="mt-1.5 flex flex-wrap gap-1">
              <Button size="small" onClick={() => S.setModal({ kind: 'flag', ma: c.ma })}>Gắn cờ</Button>
              <Button size="small" onClick={() => S.setModal({ kind: 'task', ma: c.ma })}>+ Tạo việc</Button>
            </div>
          </>
        )}
      </div>
      <Hint>
        <b>Gợi ý việc nên làm</b> <Q>bộ quy tắc gợi ý</Q>
        {p.goiy.map((g, i) => <div key={i}>· <Rich text={g} /></div>)}
      </Hint>
      <Tabs size="small" activeKey={eff} onChange={(k) => S.setSub(c.ma, k)} items={items} />
      <div>{body}</div>
    </div>
  );
};

export const ProfilePanel: React.FC<{ docked: boolean }> = ({ docked }) => {
  const S = useS();
  const { dock, call } = S;
  const ord = [...dock.tabs].sort((a, b) => (call?.ma === b.ma ? 1 : 0) - (call?.ma === a.ma ? 1 : 0));
  const act = dock.tabs.find((t) => t.ma === dock.act) ?? dock.tabs[0];
  if (!act) return null;
  return (
    <>
      <div className="flex min-h-10 items-stretch border-b border-[#C9D8E2] bg-[#EEF5F8] dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-1 flex-wrap" role="tablist">
          {ord.map((t) => {
            const cu = CUS[t.ma];
            const isCall = call?.ma === t.ma;
            return (
              <div
                key={t.ma}
                role="tab"
                aria-selected={t.ma === act.ma}
                onClick={() => S.activate(t.ma)}
                className={cx(
                  'flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-r border-[#C9D8E2] px-2.5 py-1.5 text-[12.5px] dark:border-slate-700',
                  isCall ? 'border-b-[3px] border-b-[#E07B00] bg-[#1F4E79] text-white'
                    : t.ma === act.ma ? 'border-b-[3px] border-b-[#2E75B6] bg-white font-bold dark:bg-slate-900'
                      : cx('bg-[#EEF5F8] dark:bg-slate-800', TXT_NAVY),
                )}
              >
                {isCall && <span className="rounded bg-[#E07B00] px-1 text-xs text-white">Đang gọi</span>}
                {cu.ten} · {cu.ma.slice(3)}{isCall && call ? ` · ${fmt(call.sec)}` : ''}
                <button aria-label={`Đóng ${cu.ten}`} className="rounded px-0.5 hover:bg-black/10" onClick={(e) => { e.stopPropagation(); S.closeTab(t.ma); }}><X size={13} /></button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-1 px-1.5">
          {docked && <><Button size="small" onClick={() => S.setDrw(30)}>Thu nhỏ</Button><Button size="small" onClick={() => S.setDrw(60)}>Mở rộng</Button></>}
          <Button size="small" aria-label="Đóng ngăn hồ sơ" icon={<PanelRightClose size={14} />} onClick={S.escClose}>{docked ? 'Đóng' : null}</Button>
        </div>
      </div>
      <div className="bg-[#FFF4E5] py-0.5 text-center text-xs font-bold text-[#8A4B00] dark:bg-amber-950/40 dark:text-amber-300">DỮ LIỆU MINH HOẠ</div>
      <div className="flex-1 overflow-y-auto px-3 pb-6 pt-2.5">
        {S.dmsg && <Alert className="!mb-2" type={S.dmsg.ok ? 'success' : 'error'} showIcon={false} message={<b>{S.dmsg.t}</b>} />}
        <ProfileBody key={act.ma} c={CUS[act.ma]} sub={act.sub} />
      </div>
    </>
  );
};